<template>
  <div class="my-10">
    <RateModal @rated="setUserRate" :item-id="currentId" origin="media"></RateModal>

    <div class="w-full flex gap-4 divide-y max-lg:flex-col">
      <div class="w-full">
        <div>
          <video
            controlsList="nodownload"
            controls
            :src="detail?.video"
            class="object-contain w-full h-[31.25rem]"
            alt=""
          ></video>
          <div
            class="pt-4"
            :class="{ 'p-3 bg-gradient-to-br from-[#ECECFE] to-[#D3D5FD]': detail?.is_vip }"
          >
            <div>
              <div class="flex flex-row md:flex-row justify-between items-center mb-3">
                <p class="text-xl text-main-color font-semibold mb-3 md:mb-0">{{ detail?.name }}</p>
                <button
                  class="ml-auto hover:bg-gray-100 active:bg-gray-200 px-2 rounded-full"
                  @click="copy(source)"
                >
                  <IconShare v-if="!copied" />
                  <span v-else>Link copied!</span>
                </button>
              </div>

              <div class="flex md:flex-row justify-between items-center text-main-color mb-3">
                <p class="font-light text-sm mb-3 md:mb-0">{{ detail?.date_created }}</p>
                <div class="flex items-center gap-1">
                  <IconEye />
                  <p class="text-sm font-semibold">{{ detail?.view_count }}</p>
                </div>
              </div>
            </div>
            <div>
              <div class="flex items-center justify-between pt-2">
                <div class="flex flex-col md:flex-row gap-3 items-center">
                  <a
                    v-if="detail?.link_type == 0"
                    :href="getExternalLink(detail?.link)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-lg text-[#409DBA] font-semibold mb-3 md:mb-0 hover:text-main-color"
                  >
                    {{ detail?.link_text }}
                  </a>
                  <router-link
                    v-else
                    :to="{
                      name: getRouteParams(detail).name,
                      params: getRouteParams(detail).params,
                      query: getRouteParams(detail).query,
                    }"
                    class="text-lg text-[#409DBA] font-semibold mb-3 md:mb-0 hover:text-main-color"
                    >{{ detail?.link_text }}
                  </router-link>

                  <div
                    v-if="detail?.is_vip"
                    class="w-max rounded px-3 bg-[#7951F9] text-white font-bold text-2xl text-end"
                  >
                    VIP
                  </div>
                  <button
                    v-if="detail?.subscription !== ''"
                    class="w-max rounded py-1 px-3 font-bold"
                    :class="getSubcriptionType?.color"
                  >
                    {{ getSubcriptionType?.name }}
                  </button>
                </div>
                <div class="flex gap-2">
                  <VueStarRating
                    :rounded-corners="true"
                    :star-size="15"
                    active-color="#ffb300"
                    inactive-color="#f2f2f2"
                    :border-width="3"
                    border-color="#ffb300"
                    :show-rating="false"
                    :read-only="true"
                    :increment="0.5"
                    :rating="detail?.rating"
                  ></VueStarRating>
                  <button
                    :disabled="loading"
                    class="rounded-full bg-[#409DBA] h-[2.1875rem] w-[2.1875rem] flex justify-center items-center cursor-pointer"
                    @click="likeAction"
                  >
                    <IconHeart :class="{ 'fill-white': this.isLiked }"></IconHeart>
                  </button>
                </div>
              </div>
            </div>
            <div>
              <div class="flex items-center justify-between pt-2">
                <div class="flex items-center gap-2">
                  <p>{{ $t('page.yourRate') }}:</p>
                  <VueStarRating
                    :rounded-corners="true"
                    :star-size="15"
                    active-color="#ffb300"
                    inactive-color="#f2f2f2"
                    :border-width="2"
                    border-color="#ffb300"
                    :show-rating="false"
                    :increment="1"
                    :read-only="true"
                    :rating="userRating"
                  ></VueStarRating>
                </div>
                <RateButton
                  class="bg-[#ffa700] text-base !rounded-md !py-1 px-4 w-max text-white"
                ></RateButton>
              </div>
            </div>
          </div>
        </div>
        <div class="pt-5">
          <p class="font-light text-xl text-[#333] w-full mb-5">{{ detail?.description }}</p>

          <PosterBannerSmall
            v-if="publicationSmallPosters.length > 0"
            :content="publicationSmallPosters"
          />

          <p
            v-if="
              recommendations?.mall_stores?.length > 0 &&
              recommendations?.exhibition_stores?.length > 0 &&
              recommendations?.business_accounts?.length > 0
            "
            class="mt-10 font-semibold text-main-color text-3xl"
          >
            {{ $t('page.selecteds') }}
          </p>
          <div class="mt-5" v-if="recommendations?.mall_stores?.length > 0">
            <div class="grid grid-cols-3 gap-5 max-lg:grid-cols-2 max-md:grid-cols-2">
              <router-link
                v-for="item in recommendations?.mall_stores"
                :key="item?.id"
                :to="{ name: 'storeDetail', params: { id: item?.id } }"
              >
                <StoreCard :detail="item"></StoreCard>
              </router-link>
            </div>
          </div>

          <div class="mt-5" v-if="recommendations?.exhibition_stores?.length > 0">
            <div class="grid grid-cols-3 gap-5 max-lg:grid-cols-2 max-md:grid-cols-2">
              <router-link
                v-for="item in recommendations?.exhibition_stores"
                :key="item?.id"
                :to="{ name: 'foodRestaurantDetail', params: { id: item?.id } }"
              >
                <StoreCard :detail="item"> </StoreCard>
              </router-link>
            </div>
          </div>

          <div class="mt-5" v-if="recommendations?.business_accounts?.length > 0">
            <div class="grid grid-cols-3 gap-5 max-lg:grid-cols-2 max-md:grid-cols-2">
              <router-link
                v-for="item in recommendations?.business_accounts"
                :key="item.id"
                :to="{ name: 'businessAccountsDetail', params: { id: item.id } }"
              >
                <BusinessAccountCard :detail="item"> </BusinessAccountCard>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="w-2/5 max-lg:w-full">
        <p class="text-main-color text-2xl font-semibold mb-3">{{ $t('page.similars') }}</p>
        <div class="grid grid-cols-1 gap-5 max-lg:grid-cols-2">
          <router-link
            v-for="(video, index) in relatedVideos"
            :key="video.id"
            :to="{ name: 'mediaDetail', params: { id: video.id } }"
          >
            <MediaCard :detail="video" />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate } from '@/helpers';
import Modal from '@cmp/Modal.vue';
import PosterBannerSmall from '@cmp/PosterBannerSmall.vue';
import api from '@/api';
import MediaCard from '@cmp/media/MediaCard.vue';
import IconEye from '@icons/IconEye.vue';
import IconHeart from '@icons/IconHeart.vue';
import IconShare from '@icons/IconShare.vue';
import VueStarRating from 'vue-star-rating';
import StoreCard from '@cmp/mall/StoreCard.vue';
import BusinessAccountCard from '@cmp/media/BusinessAccountCard.vue';
import RateModal from '@cmp/modals/RateModal.vue';
import RateButton from '@cmp/modals/buttons/RateButton.vue';

import { ref } from 'vue';
import { useClipboard } from '@vueuse/core';
import { getRouteParams, getExternalLink } from '@/helpers';

export default {
  name: 'mediaDetail',
  components: {
    MediaCard,
    PosterBannerSmall,
    IconEye,
    IconHeart,
    IconShare,
    VueStarRating,
    Modal,
    StoreCard,
    BusinessAccountCard,
    RateModal,
    RateButton,
  },
  watch: {
    '$route.params.id': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.currentId = newValue;
          this.getVideoDetail();
        }
      },
    },
  },
  setup() {
    const source = ref(window.location.href);
    const { text, copy, copied, isSupported } = useClipboard({ source });
    return {
      text,
      copy,
      copied,
      isSupported,
    };
  },
  data() {
    return {
      loading: false,
      currentId: null,
      detail: null,
      isLiked: false,
      rateModal: false,
      userRating: 0,
      publicationSmallPosters: [],
      relatedVideos: [],
      recommendations: [],
      formRate: {
        rating: 0,
      },
    };
  },
  methods: {
    formatDate,
    getRouteParams,
    getExternalLink,
    openRateModal() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      this.rateModal = true;
    },
    getLocationId() {
      const location = JSON.parse(localStorage.getItem('selectedLocation')) || 1;
      return location?.id || location;
    },

    closeRateModal() {
      this.rateModal = false;
      this.formRate = {
        rating: 0,
      };
    },
    async getVideoDetail() {
      this.loading = true;
      try {
        const response = await api.get(`posts/videos/${this.currentId}/`);
        this.detail = response.data;
        this.isLiked = this.detail.is_liked;
        this.formRate.rating = this.detail?.user_rating?.rating;
        this.userRating = this.detail?.user_rating?.rating;
        await this.getRelatedVideos();
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    async likeAction() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        this.loading = true;
        const response = await api.post(`posts/videos/${this.currentId}/like/`, {
          is_liked: !this.isLiked,
        });
        this.isLiked = !this.isLiked;
        this.$store.getLiked('videos');
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    async getPublicationSmallPoster() {
      try {
        const response = await api.get('commercials/advertisements/', {
          params: {
            type: 213,
            location: this.getLocationId(),
          },
        });

        this.publicationSmallPosters = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    async getRelatedVideos() {
      try {
        const response = await api.get('posts/videos/', {
          params: { location: this.getLocationId() },
        });
        this.relatedVideos = response.data.results;
      } catch (e) {
        console.log(e);
      }
    },
    async getRecommendations() {
      try {
        const response = await api.get(`posts/videos/recommendations/`);
        this.recommendations = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    setUserRate(value) {
      this.userRating = value;
    },
    async postVideoRate() {
      try {
        const response = await api.post(`posts/videos/${this.currentId}/rate/`, this.formRate);
        // this.$toast.info(`${i18n.global.t('warning.PUBLICATION_REPORTED')}`)
        this.closeRateModal();
        this.userRating = response.data.rating;
      } catch (e) {
        console.log(e);
      }
    },
  },
  computed: {
    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          break;
      }
    },
    getLocation() {
      return JSON.parse(localStorage.getItem('selectedLocation')) || 1;
    },
  },
  async created() {
    this.currentId = this.$route.params.id;

    await this.getVideoDetail();
    await this.getPublicationSmallPoster();
    await this.getRecommendations();
  },
};
</script>
